import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Play,
  Plus,
  Trash2,
  Settings,
  Clock,
  ArrowRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  FileText,
  FileSignature,
  Mail,
  Calendar,
  CreditCard,
  FileSpreadsheet,
  Ticket,
  CheckSquare,
  Tag,
  GitCommit,
  FolderPlus,
  ShieldCheck,
  AlertTriangle,
  Copy,
  X,
  Undo2,
  Redo2,
  LayoutGrid,
  Unlink,
  ChevronDown,
  RefreshCw,
} from 'lucide-react';
import {
  AutomationActionType,
  AutomationEdge,
  AutomationNode,
} from '../../../types/automation';
import { AutomationNodeModal } from './AutomationNodeModal';
import {
  addConnection,
  autoArrangeNodes,
  buildChainEdges,
  computeNodeSequenceMap,
  deleteNodeAndReconnect,
  duplicateNode,
  generateNodeSummary,
  removeConnection,
} from './automationChain';

interface AutomationCanvasProps {
  nodes: AutomationNode[];
  edges: AutomationEdge[];
  onChange: (nodes: AutomationNode[], edges: AutomationEdge[]) => void;
  onTest?: () => void;
  readOnly?: boolean;
}

const ACTION_ICONS: Record<
  AutomationActionType,
  React.ComponentType<{ className?: string }>
> = {
  generate_document: FileText,
  send_sign_request: FileSignature,
  send_email: Mail,
  create_event: Calendar,
  create_invoice: CreditCard,
  create_quote: FileSpreadsheet,
  create_support_ticket: Ticket,
  create_internal_task: CheckSquare,
  tag_application: Tag,
  change_workflow_stage: GitCommit,
  update_application_status: RefreshCw,
  add_to_batch: FolderPlus,
  update_batch: FolderPlus,
  update_admin: ShieldCheck,
};

const ACTION_TYPES: Array<{ type: AutomationActionType; label: string; desc: string }> = [
  { type: 'generate_document', label: 'Generate Document', desc: 'Create applicant PDF document' },
  { type: 'send_sign_request', label: 'Send Sign Request', desc: 'Send built-in native e-signature request' },
  { type: 'send_email', label: 'Send Email', desc: 'Send template or custom email' },
  { type: 'create_event', label: 'Create Event', desc: 'Schedule interview or meeting' },
  { type: 'create_invoice', label: 'Create Invoice', desc: 'Generate Finance payment invoice' },
  { type: 'create_quote', label: 'Create Quote', desc: 'Issue formal program price quote' },
  { type: 'create_support_ticket', label: 'Support Ticket', desc: 'Raise issue or follow-up ticket' },
  { type: 'create_internal_task', label: 'Internal Task', desc: 'Assign task to team member' },
  { type: 'tag_application', label: 'Tag Application', desc: 'Add or remove applicant tags' },
  { type: 'change_workflow_stage', label: 'Change Stage', desc: 'Advance candidate workflow stage' },
  { type: 'update_application_status', label: 'Update Application Status', desc: 'Change application main status (Draft, Submitted, Returned, Cancelled, Closed, Archived)' },
  { type: 'update_batch', label: 'Update Batch', desc: 'Add candidates to a cohort batch, or replace its members' },
  { type: 'update_admin', label: 'Update Admin', desc: 'Modify or remove a workspace team administrator' },
];

const NODE_WIDTH = 250;
const NODE_HEIGHT = 120;

export const AutomationCanvas: React.FC<AutomationCanvasProps> = ({
  nodes,
  edges,
  onChange,
  onTest,
  readOnly = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Viewport & Zoom State
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 40, y: 40 });
  const [isPanning, setIsPanning] = useState<boolean>(false);

  // Node Selection & Editing
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [editingNode, setEditingNode] = useState<AutomationNode | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);

  // Live position of the node currently being dragged. Kept canvas-local
  // while a drag is in flight so the node (and its edges, whose endpoints
  // derive from effective positions) stays glued to the cursor; the parent
  // graph is committed exactly once, on release.
  const [dragPos, setDragPos] = useState<{ id: string; x: number; y: number } | null>(null);

  // Drag-to-Connect Edge State
  const [connectingSourceId, setConnectingSourceId] = useState<string | null>(null);
  const [connectionCursorPos, setConnectionCursorPos] = useState<{ x: number; y: number } | null>(null);
  const [hoveredTargetId, setHoveredTargetId] = useState<string | null>(null);
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  // Click-selected edge. Selection (not hover) owns the persistent delete
  // control: hover alone used to unmount the × the moment the cursor moved
  // onto it, because the × sits on top of the very path that tracked hover.
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [connectionFeedback, setConnectionFeedback] = useState<string | null>(null);
  // The implicit Start connection (rendered when the stored edge set is
  // empty — see implicitStartEdge below) has no stored edge to delete:
  // its stored representation IS the empty edge set. Dismissing it is
  // therefore canvas session state, keyed by the first node's id: while
  // dismissed, the guide line does not render for that first node. It
  // clears itself whenever a different node becomes first (the key no
  // longer matches) or a stored edge set exists (the implicit edge no
  // longer applies). Known representation limit, stated in the PR: the
  // save payload has no "start disconnected" marker, so for an
  // edge-less graph the dismissal does not survive a reload — the
  // guide returns, because a root node with no incoming edge IS
  // start-triggered at execution time. Disconnects of a STORED Start
  // edge persist normally (the edge is filtered from the payload).
  const [dismissedImplicitStartTarget, setDismissedImplicitStartTarget] = useState<string | null>(null);

  // Undo / Redo History Stack
  const [history, setHistory] = useState<Array<{ nodes: AutomationNode[]; edges: AutomationEdge[] }>>([
    { nodes, edges },
  ]);
  const [historyIndex, setHistoryIndex] = useState<number>(0);

  // Keep local history synchronized on significant mutations
  const updateGraph = useCallback(
    (newNodes: AutomationNode[], newEdges: AutomationEdge[]) => {
      onChange(newNodes, newEdges);
      setHistory((prev) => {
        const sliced = prev.slice(0, historyIndex + 1);
        return [...sliced, { nodes: newNodes, edges: newEdges }];
      });
      setHistoryIndex((prev) => prev + 1);
    },
    [historyIndex, onChange]
  );

  const handleUndo = () => {
    if (historyIndex > 0) {
      const prev = history[historyIndex - 1];
      setHistoryIndex(historyIndex - 1);
      onChange(prev.nodes, prev.edges);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const next = history[historyIndex + 1];
      setHistoryIndex(historyIndex + 1);
      onChange(next.nodes, next.edges);
    }
  };

  // ---------------------------------------------------------------------
  // Pointer gesture machinery.
  //
  // Every canvas gesture (node drag, drag-to-connect, background pan) runs
  // through ONE ref-held state machine fed by window-level pointermove /
  // pointerup listeners. A gesture therefore starts tracking from the very
  // first pixel (no waiting on a state commit) and still finishes cleanly
  // when the pointer is released outside the container. Visual updates are
  // batched through requestAnimationFrame and kept canvas-local; the parent
  // graph (onChange + history) is committed exactly once, on release.
  // ---------------------------------------------------------------------

  type Gesture =
    | { kind: 'pan'; startClientX: number; startClientY: number; startPanX: number; startPanY: number }
    | {
        kind: 'node';
        nodeId: string;
        startClientX: number;
        startClientY: number;
        grabOffsetX: number;
        grabOffsetY: number;
        active: boolean;
      }
    | { kind: 'connect'; sourceId: string };

  const gestureRef = useRef<Gesture | null>(null);
  const rafRef = useRef<number | null>(null);
  const pendingFrameRef = useRef<(() => void) | null>(null);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Latest-value mirrors so the window listeners never read stale closures.
  const viewRef = useRef({ pan, zoomLevel });
  viewRef.current = { pan, zoomLevel };
  const graphRef = useRef({ nodes, edges });
  graphRef.current = { nodes, edges };
  const updateGraphRef = useRef(updateGraph);
  updateGraphRef.current = updateGraph;

  const scheduleFrame = useCallback((fn: () => void) => {
    pendingFrameRef.current = fn;
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const run = pendingFrameRef.current;
        pendingFrameRef.current = null;
        if (run) run();
      });
    }
  }, []);

  const cancelPendingFrame = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pendingFrameRef.current = null;
  }, []);

  // Convert client (screen) coordinates to canvas coordinates, accounting
  // for the container offset, pan and zoom.
  const coordsFromClient = useCallback((clientX: number, clientY: number) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    const { pan: p, zoomLevel: z } = viewRef.current;
    return {
      x: (clientX - rect.left - p.x) / z,
      y: (clientY - rect.top - p.y) / z,
    };
  }, []);

  // Canvas position the dragged node occupies for a pointer at (clientX, clientY).
  const nodePosFromClient = useCallback(
    (g: Extract<Gesture, { kind: 'node' }>, clientX: number, clientY: number) => {
      const coords = coordsFromClient(clientX, clientY);
      return {
        x: Math.max(20, Math.round(coords.x - g.grabOffsetX)),
        y: Math.max(20, Math.round(coords.y - g.grabOffsetY)),
      };
    },
    [coordsFromClient]
  );

  // Geometric drop-target test for drag-to-connect. Dropping anywhere on a
  // node's card (expanded slightly) targets that node; otherwise the
  // nearest input anchor within a generous radius wins. The source node is
  // excluded. This replaces the old elementFromPoint lookup, which only
  // connected when the pointer landed exactly on the 16px input dot.
  const findConnectTarget = useCallback((pt: { x: number; y: number }, sourceId: string): string | null => {
    const { nodes: currentNodes } = graphRef.current;
    let bestId: string | null = null;
    let bestDist = Infinity;
    for (const n of currentNodes) {
      if (n.id === sourceId) continue;
      const anchorX = n.position.x - 8;
      const anchorY = n.position.y + 40;
      const dist = Math.hypot(pt.x - anchorX, pt.y - anchorY);
      const onCard =
        pt.x >= n.position.x - 12 &&
        pt.x <= n.position.x + NODE_WIDTH + 12 &&
        pt.y >= n.position.y - 12 &&
        pt.y <= n.position.y + NODE_HEIGHT + 12;
      if ((onCard || dist <= 30) && dist < bestDist) {
        bestDist = dist;
        bestId = n.id;
      }
    }
    return bestId;
  }, []);

  const handleGestureMove = useCallback(
    (e: PointerEvent) => {
      const g = gestureRef.current;
      if (!g) return;

      if (g.kind === 'pan') {
        const nextPan = {
          x: g.startPanX + (e.clientX - g.startClientX),
          y: g.startPanY + (e.clientY - g.startClientY),
        };
        scheduleFrame(() => setPan(nextPan));
        return;
      }

      if (g.kind === 'node') {
        if (!g.active) {
          const moved = Math.hypot(e.clientX - g.startClientX, e.clientY - g.startClientY);
          if (moved < 4) return; // below the threshold this is still a click
          g.active = true;
        }
        const pos = nodePosFromClient(g, e.clientX, e.clientY);
        scheduleFrame(() => setDragPos({ id: g.nodeId, x: pos.x, y: pos.y }));
        return;
      }

      // Drag-to-connect: move the preview line and track the drop target.
      const pt = coordsFromClient(e.clientX, e.clientY);
      const targetId = findConnectTarget(pt, g.sourceId);
      scheduleFrame(() => {
        setConnectionCursorPos(pt);
        setHoveredTargetId((prev) => (prev === targetId ? prev : targetId));
      });
    },
    [coordsFromClient, findConnectTarget, nodePosFromClient, scheduleFrame]
  );

  const finishGesture = useCallback(
    (e: PointerEvent | null, cancelled: boolean) => {
      const g = gestureRef.current;
      gestureRef.current = null;
      cancelPendingFrame();
      if (!g) return;

      if (g.kind === 'pan') {
        // Land the pan exactly on the release point, not on whichever
        // animation frame happened to flush last.
        if (!cancelled && e) {
          setPan({
            x: g.startPanX + (e.clientX - g.startClientX),
            y: g.startPanY + (e.clientY - g.startClientY),
          });
        }
        setIsPanning(false);
        return;
      }

      if (g.kind === 'node') {
        if (g.active && !cancelled && e) {
          // Commit exactly once, from the release point itself.
          const pos = nodePosFromClient(g, e.clientX, e.clientY);
          const { nodes: currentNodes, edges: currentEdges } = graphRef.current;
          const node = currentNodes.find((n) => n.id === g.nodeId);
          if (node && (node.position.x !== pos.x || node.position.y !== pos.y)) {
            const updatedNodes = currentNodes.map((n) =>
              n.id === g.nodeId ? { ...n, position: { x: pos.x, y: pos.y } } : n
            );
            updateGraphRef.current(updatedNodes, currentEdges);
          }
        }
        setDragPos(null);
        return;
      }

      // Drag-to-connect: hit-test fresh at the release point (never from
      // hover state, which can lag a fast move-and-release).
      if (!cancelled && e) {
        const pt = coordsFromClient(e.clientX, e.clientY);
        const targetId = findConnectTarget(pt, g.sourceId);
        if (targetId && targetId !== g.sourceId) {
          const { nodes: currentNodes, edges: currentEdges } = graphRef.current;
          const res = addConnection(currentNodes, currentEdges, g.sourceId, targetId);
          if (res.success) {
            updateGraphRef.current(res.nodes, res.edges);
            setConnectionFeedback(null);
          } else if (res.reason) {
            setConnectionFeedback(res.reason);
            if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
            feedbackTimerRef.current = setTimeout(() => setConnectionFeedback(null), 3500);
          }
        }
      }
      setConnectingSourceId(null);
      setConnectionCursorPos(null);
      setHoveredTargetId(null);
    },
    [cancelPendingFrame, coordsFromClient, findConnectTarget, nodePosFromClient]
  );

  useEffect(() => {
    const onMove = (e: PointerEvent) => handleGestureMove(e);
    const onUp = (e: PointerEvent) => finishGesture(e, false);
    const onCancel = () => finishGesture(null, true);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('blur', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('blur', onCancel);
    };
  }, [handleGestureMove, finishGesture]);

  useEffect(() => {
    return () => {
      cancelPendingFrame();
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, [cancelPendingFrame]);

  // The IMPLICIT Start edge. A graph whose stored edge set is empty
  // still draws a Start -> first-node connection (it is how every
  // UI-built flow looks: handleAddAction stores step-to-step edges
  // only, never a Start edge for the first node). That connection is a
  // first-class edge with the conventional id `edge_start_<firstId>`
  // (the id addConnection would give it): it renders with the same
  // wide hit path, the same selection, the same overlay × and the
  // same Delete-key path as any stored edge. It exists only while the
  // stored set is empty and it has not been dismissed (see
  // dismissedImplicitStartTarget above).
  const implicitStartEdge: AutomationEdge | null =
    edges.length === 0 &&
    nodes.length > 0 &&
    dismissedImplicitStartTarget !== nodes[0].id
      ? { id: `edge_start_${nodes[0].id}`, source: 'start', target: nodes[0].id }
      : null;
  // The delete overlay iterates stored edges plus the implicit Start
  // edge (when present), so its × renders through the identical path.
  const overlayEdges: AutomationEdge[] = implicitStartEdge
    ? [...edges, implicitStartEdge]
    : edges;

  // Keyboard Shortcuts for Undo/Redo & Delete
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (readOnly) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') {
        handleRedo();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        // The key is consumed by the canvas whenever it deletes (or
        // could delete) — never left for the browser to interpret as
        // navigation/editing elsewhere, which reads as a deselect.
        e.preventDefault();
        if (
          selectedEdgeId &&
          (edges.some((edge) => edge.id === selectedEdgeId) ||
            selectedEdgeId === implicitStartEdge?.id)
        ) {
          // A selected edge takes the Delete key over a selected node;
          // the two selections are mutually exclusive in the UI.
          handleRemoveEdge(selectedEdgeId);
          setSelectedEdgeId(null);
        } else if (selectedNodeId) {
          handleRemoveNode(selectedNodeId);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodeId, selectedEdgeId, edges, readOnly, historyIndex, history, implicitStartEdge?.id]);

  // Compute sequence step map for sequence numbers (1, 2, 3...)
  const sequenceMap = computeNodeSequenceMap(nodes, edges);

  // Add a new Action node
  const handleAddAction = (actionType: AutomationActionType) => {
    const newNodeId = `node_${Date.now()}`;
    const lastNode = nodes.length > 0 ? nodes[nodes.length - 1] : null;

    const newX = lastNode ? lastNode.position.x + 280 : 200;
    const newY = lastNode ? Math.max(20, lastNode.position.y) : 180;

    const actionInfo = ACTION_TYPES.find((a) => a.type === actionType);

    const newNode: AutomationNode = {
      id: newNodeId,
      type: 'action',
      position: { x: newX, y: newY },
      data: {
        label: actionInfo?.label || 'New Action',
        actionType,
        delayValue: 0,
        delayUnit: 'hours',
        config: {},
      },
    };

    let newEdges = [...edges];
    if (lastNode) {
      newEdges = [...edges, { id: `edge_${lastNode.id}_${newNodeId}`, source: lastNode.id, target: newNodeId }];
    }

    const newNodes = [...nodes, newNode];
    updateGraph(newNodes, newEdges);

    // Keep newly created node visible in current viewport
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const visibleRight = (containerWidth - pan.x) / zoomLevel;
      if (newX + NODE_WIDTH + 60 > visibleRight) {
        const requiredPanX = containerWidth - (newX + NODE_WIDTH + 60) * zoomLevel;
        setPan((prev) => ({ ...prev, x: requiredPanX }));
      }
    }

    setEditingNode(newNode);
    setIsModalOpen(true);
    setAddMenuOpen(false);
  };

  // Node Duplicate
  const handleDuplicate = (nodeId: string) => {
    const res = duplicateNode(nodes, edges, nodeId);
    if (res.newNodeId) {
      updateGraph(res.nodes, res.edges);
      setSelectedNodeId(res.newNodeId);
      const dupNode = res.nodes.find((n) => n.id === res.newNodeId);
      if (dupNode && containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const visibleRight = (containerWidth - pan.x) / zoomLevel;
        if (dupNode.position.x + NODE_WIDTH + 60 > visibleRight) {
          const requiredPanX = containerWidth - (dupNode.position.x + NODE_WIDTH + 60) * zoomLevel;
          setPan((prev) => ({ ...prev, x: requiredPanX }));
        }
      }
    }
  };

  // Node Delete
  const handleRemoveNode = (nodeId: string) => {
    const res = deleteNodeAndReconnect(nodes, edges, nodeId);
    updateGraph(res.nodes, res.edges);
    if (selectedNodeId === nodeId) setSelectedNodeId(null);
  };

  // Save Config Data from Modal
  const handleSaveNodeData = (nodeData: any) => {
    if (!editingNode) return;
    const newNodes = nodes.map((n) => (n.id === editingNode.id ? { ...n, data: nodeData } : n));
    updateGraph(newNodes, edges);
    setEditingNode(null);
  };

  // Auto Arrange & Recenter in Viewport
  const handleAutoArrange = () => {
    // Tidy in place: a pure position transform anchored to the current
    // layout (sequence columns, lanes preserved, fixed point). The live
    // edge set passes through untouched (Start edge included) and is
    // what reaches the save payload.
    const res = autoArrangeNodes(nodes, edges);
    updateGraph(res.nodes, res.edges);

    const arrangedNodes = res.nodes;
    if (arrangedNodes.length === 0) return;

    // Start node is at x: 40, y: 180 (w: 112, h: 80)
    let minX = 40;
    let maxX = 40 + 112;
    let minY = 180;
    let maxY = 180 + 80;

    arrangedNodes.forEach((n) => {
      minX = Math.min(minX, n.position.x);
      maxX = Math.max(maxX, n.position.x + NODE_WIDTH);
      minY = Math.min(minY, n.position.y);
      maxY = Math.max(maxY, n.position.y + NODE_HEIGHT);
    });

    const padding = 80;
    const contentWidth = maxX - minX + padding * 2;
    const contentHeight = maxY - minY + padding * 2;

    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    const scaleX = containerWidth / contentWidth;
    const scaleY = containerHeight / contentHeight;
    const newZoom = Math.max(0.5, Math.min(1.1, Math.min(scaleX, scaleY)));

    const newPanX = (containerWidth - (maxX - minX) * newZoom) / 2 - minX * newZoom;
    const newPanY = (containerHeight - (maxY - minY) * newZoom) / 2 - minY * newZoom;

    setZoomLevel(newZoom);
    setPan({ x: newPanX, y: newPanY });
  };

  // Fit view to center all nodes
  const handleFitView = () => {
    if (nodes.length === 0) {
      setPan({ x: 40, y: 40 });
      setZoomLevel(1);
      return;
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    nodes.forEach((n) => {
      minX = Math.min(minX, n.position.x);
      maxX = Math.max(maxX, n.position.x + NODE_WIDTH);
      minY = Math.min(minY, n.position.y);
      maxY = Math.max(maxY, n.position.y + NODE_HEIGHT);
    });

    const padding = 80;
    const contentWidth = maxX - minX + padding * 2;
    const contentHeight = maxY - minY + padding * 2;

    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    const scaleX = containerWidth / contentWidth;
    const scaleY = containerHeight / contentHeight;
    const newZoom = Math.max(0.6, Math.min(1.2, Math.min(scaleX, scaleY)));

    const newPanX = (containerWidth - contentWidth * newZoom) / 2 - (minX - padding) * newZoom;
    const newPanY = (containerHeight - contentHeight * newZoom) / 2 - (minY - padding) * newZoom;

    setZoomLevel(newZoom);
    setPan({ x: newPanX, y: newPanY });
  };

  // Pointer Down on a Node Card: select immediately and arm a potential
  // drag. The gesture state machine promotes it to a real drag only after
  // a small movement threshold, so a plain click still just selects.
  const handleNodePointerDown = (e: React.PointerEvent, nodeId: string) => {
    setSelectedNodeId(nodeId);
    setSelectedEdgeId(null);
    if (readOnly) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // Presses on the card's action buttons select, but never start a drag.
    if ((e.target as HTMLElement).closest('button')) return;

    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const coords = coordsFromClient(e.clientX, e.clientY);
    gestureRef.current = {
      kind: 'node',
      nodeId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      grabOffsetX: coords.x - node.position.x,
      grabOffsetY: coords.y - node.position.y,
      active: false,
    };
  };

  // Pointer Down on an Output Handle: begin a drag-to-connect gesture.
  const handleOutputHandlePointerDown = (e: React.PointerEvent, sourceId: string) => {
    if (readOnly) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    gestureRef.current = { kind: 'connect', sourceId };
    setConnectingSourceId(sourceId);
    setConnectionCursorPos(coordsFromClient(e.clientX, e.clientY));
  };

  // Pointer Down on the Canvas Background: clear selection and begin
  // panning. Node and connection gestures start on their own elements and
  // never reach this check (their targets are not the background).
  const handleCanvasPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // The edge delete layer's subtree is exempt from gesture
    // initiation: a press there deletes an edge (handled at
    // pointerdown on the control itself) and must never start a pan
    // or clear the selection it acts on. Its controls stopPropagation
    // first; this is the structural backstop.
    if ((e.target as HTMLElement).closest('[data-testid="edge-delete-layer"]')) return;
    if (e.target === containerRef.current || (e.target as HTMLElement).getAttribute('data-canvas-bg') === 'true') {
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
      setAddMenuOpen(false);
      setIsPanning(true);
      gestureRef.current = {
        kind: 'pan',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startPanX: pan.x,
        startPanY: pan.y,
      };
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? 1.05 : 0.95;
      setZoomLevel((z) => Math.max(0.5, Math.min(1.5, z * zoomFactor)));
    } else {
      setPan((p) => ({
        x: p.x - e.deltaX * 0.8,
        y: p.y - e.deltaY * 0.8,
      }));
    }
  };

  // Remove single Edge Line
  const handleRemoveEdge = (edgeId: string) => {
    if (readOnly) return;
    if (implicitStartEdge && edgeId === implicitStartEdge.id) {
      // The implicit Start connection has no stored edge to filter —
      // its stored representation IS the empty edge set, so there is
      // no graph change to commit (and Auto Save correctly fires
      // nothing). Dismiss it for this canvas session instead; see the
      // representation-limit note on dismissedImplicitStartTarget.
      setDismissedImplicitStartTarget(implicitStartEdge.target);
      return;
    }
    const res = removeConnection(nodes, edges, edgeId);
    updateGraph(res.nodes, res.edges);
  };

  // Effective on-canvas position of a node: its live drag position while a
  // drag is in flight (so cards and edge endpoints track the cursor
  // together), otherwise its committed position.
  const effectiveNodePos = (node: AutomationNode) =>
    dragPos && dragPos.id === node.id ? { x: dragPos.x, y: dragPos.y } : node.position;

  // Calculate handle connection coordinates for SVG lines
  const getOutputHandlePos = (nodeId: string) => {
    if (nodeId === 'start') {
      return { x: 120, y: 220 };
    }
    const n = nodes.find((node) => node.id === nodeId);
    if (!n) return { x: 0, y: 0 };
    const pos = effectiveNodePos(n);
    return {
      x: pos.x + NODE_WIDTH + 8,
      y: pos.y + 40,
    };
  };

  const getInputHandlePos = (nodeId: string) => {
    const n = nodes.find((node) => node.id === nodeId);
    if (!n) return { x: 0, y: 0 };
    const pos = effectiveNodePos(n);
    return {
      x: pos.x - 8,
      y: pos.y + 40,
    };
  };

  // Start Node Position
  const startNodePos = { x: 40, y: 180 };
  const firstActionNode = nodes.length > 0 ? nodes[0] : null;
  const firstNodePos = firstActionNode ? effectiveNodePos(firstActionNode) : null;

  return (
    <div
      ref={containerRef}
      onPointerDown={handleCanvasPointerDown}
      onWheel={handleWheel}
      data-canvas-bg="true"
      className="relative w-full h-[680px] bg-slate-50 rounded-2xl overflow-hidden border border-slate-200/80 flex flex-col select-none shadow-xs"
    >
      {/* Top Floating Control Toolbar */}
      <div className="px-5 py-3 bg-white/90 border-b border-slate-200 flex items-center justify-between z-20 backdrop-blur-md shadow-xs">
        <div className="flex items-center gap-3">
          <span className="text-xs font-black text-slate-500 uppercase tracking-widest">
            Visual Flow Builder
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-indigo-50 text-indigo-700 border border-indigo-200">
            {nodes.length} Actions
          </span>
          {connectionFeedback && (
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold animate-fade-in">
              <AlertTriangle size={13} />
              {connectionFeedback}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Undo / Redo */}
          {!readOnly && (
            <div className="flex items-center bg-white rounded-xl p-0.5 border border-slate-200 shadow-2xs">
              <button
                onClick={handleUndo}
                disabled={historyIndex <= 0}
                className="p-1.5 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer"
                title="Undo (Ctrl+Z)"
                aria-label="Undo canvas change"
              >
                <Undo2 size={14} />
              </button>
              <button
                onClick={handleRedo}
                disabled={historyIndex >= history.length - 1}
                className="p-1.5 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer"
                title="Redo (Ctrl+Y)"
                aria-label="Redo canvas change"
              >
                <Redo2 size={14} />
              </button>
            </div>
          )}

          {/* Auto Layout */}
          {!readOnly && nodes.length > 0 && (
            <button
              onClick={handleAutoArrange}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold transition-all shadow-2xs cursor-pointer"
              title="Auto-arrange and recenter diagram in viewport"
            >
              <LayoutGrid size={13} className="text-slate-500" />
              Auto Align
            </button>
          )}

          {/* Zoom Controls */}
          <div className="flex items-center bg-white rounded-xl p-0.5 border border-slate-200 shadow-2xs">
            <button
              onClick={() => setZoomLevel((z) => Math.max(0.5, z - 0.1))}
              className="p-1.5 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              title="Zoom Out"
            >
              <ZoomOut size={14} />
            </button>
            <span className="px-2 text-[11px] font-mono font-bold text-slate-600">
              {Math.round(zoomLevel * 100)}%
            </span>
            <button
              onClick={() => setZoomLevel((z) => Math.min(1.5, z + 0.1))}
              className="p-1.5 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              title="Zoom In"
            >
              <ZoomIn size={14} />
            </button>
            <button
              onClick={handleFitView}
              className="p-1.5 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors ml-0.5 cursor-pointer"
              title="Fit to canvas"
            >
              <Maximize2 size={14} />
            </button>
          </div>

          {onTest && (
            <button
              onClick={onTest}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 text-xs font-bold transition-all cursor-pointer"
            >
              <Play size={13} className="fill-amber-600" />
              Test Flow
            </button>
          )}

          {/* Add Action Dropdown */}
          {!readOnly && (
            <div className="relative">
              <button
                onClick={() => setAddMenuOpen(!addMenuOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-extrabold transition-all shadow-md shadow-brand-primary/30 cursor-pointer"
              >
                <Plus size={14} />
                Add Action
                <ChevronDown size={13} />
              </button>

              {addMenuOpen && (
                <div
                  onWheel={(e) => e.stopPropagation()}
                  className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-slate-200 py-2 z-50 animate-fade-in divide-y divide-slate-100"
                >
                  <div className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400">
                    Select Automation Action
                  </div>
                  <div
                    onWheel={(e) => e.stopPropagation()}
                    className="max-h-72 overflow-y-auto py-1 overscroll-contain"
                  >
                    {ACTION_TYPES.map((act) => {
                      const Icon = ACTION_ICONS[act.type];
                      return (
                        <button
                          key={act.type}
                          onClick={() => handleAddAction(act.type)}
                          className="w-full px-3 py-2 text-left hover:bg-indigo-50/60 flex items-start gap-2.5 transition-colors cursor-pointer group"
                        >
                          <div className="p-1.5 rounded-lg bg-slate-100 group-hover:bg-indigo-100 text-slate-600 group-hover:text-indigo-600 mt-0.5">
                            <Icon className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="text-xs font-extrabold text-slate-800 group-hover:text-indigo-600">
                              {act.label}
                            </div>
                            <div className="text-[11px] text-slate-500 leading-tight">
                              {act.desc}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Interactive 2D Canvas Layer */}
      <div
        data-canvas-bg="true"
        className={`flex-1 relative overflow-hidden touch-none bg-[radial-gradient(#cbd5e1_1.2px,transparent_1.2px)] [background-size:24px_24px] ${isPanning ? 'cursor-grabbing' : 'cursor-grab active:cursor-grabbing'}`}
      >
        <div
          data-canvas-bg="true"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomLevel})`,
            transformOrigin: 'top left',
          }}
          className="absolute inset-0 min-w-[3000px] min-h-[2000px]"
        >
          {/* SVG Connection Lines Overlay */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
            <defs>
              <marker
                id="arrowhead"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#6366f1" />
              </marker>
            </defs>

            {/* Edge 0: Implicit connection from Start -> First Action
                Node, rendered when the stored edge set is empty. This
                is a FIRST-CLASS edge (see implicitStartEdge above):
                the wide invisible hit path follows the visible dashed
                curve exactly (same `d`), hover is tracked on the
                group, clicking selects, and the delete × renders in
                the top-layer overlay via overlayEdges. Before this it
                was a bare <path> in this pointer-events-none SVG with
                no hit stroke and no overlay entry at all, so the
                Start connection of an edge-less flow (AUT-10004 live)
                could never be selected or disconnected at any zoom. */}
            {implicitStartEdge && firstActionNode && firstNodePos && (
              <g
                className="group"
                onMouseEnter={() => setHoveredEdgeId(implicitStartEdge.id)}
                onMouseLeave={() =>
                  setHoveredEdgeId((prev) => (prev === implicitStartEdge.id ? null : prev))
                }
              >
                <path
                  data-testid={`edge-hit-${implicitStartEdge.id}`}
                  data-edge-selected={selectedEdgeId === implicitStartEdge.id ? 'true' : undefined}
                  d={`M ${startNodePos.x + 80} ${startNodePos.y + 40} C ${startNodePos.x + 160} ${startNodePos.y + 40}, ${firstNodePos.x - 40} ${firstNodePos.y + 40}, ${firstNodePos.x - 8} ${firstNodePos.y + 40}`}
                  fill="none"
                  stroke="transparent"
                  strokeWidth="20"
                  className="pointer-events-auto cursor-pointer"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setSelectedEdgeId(implicitStartEdge.id);
                    setSelectedNodeId(null);
                  }}
                />
                <path
                  d={`M ${startNodePos.x + 80} ${startNodePos.y + 40} C ${startNodePos.x + 160} ${startNodePos.y + 40}, ${firstNodePos.x - 40} ${firstNodePos.y + 40}, ${firstNodePos.x - 8} ${firstNodePos.y + 40}`}
                  fill="none"
                  stroke={
                    hoveredEdgeId === implicitStartEdge.id || selectedEdgeId === implicitStartEdge.id
                      ? '#4f46e5'
                      : '#94a3b8'
                  }
                  strokeWidth={
                    hoveredEdgeId === implicitStartEdge.id || selectedEdgeId === implicitStartEdge.id
                      ? '3.5'
                      : '2'
                  }
                  strokeDasharray="4 4"
                  markerEnd="url(#arrowhead)"
                  className="transition-all"
                />
              </g>
            )}

            {/* Explicit Edge Connections */}
            {edges.map((edge) => {
              const srcPos = getOutputHandlePos(edge.source);
              const tgtPos = getInputHandlePos(edge.target);

              const dx = Math.max(50, Math.abs(tgtPos.x - srcPos.x) * 0.5);
              const pathD = `M ${srcPos.x} ${srcPos.y} C ${srcPos.x + dx} ${srcPos.y}, ${tgtPos.x - dx} ${tgtPos.y}, ${tgtPos.x} ${tgtPos.y}`;

              const isHovered = hoveredEdgeId === edge.id;
              const isSelected = selectedEdgeId === edge.id;
              const isHighlighted = isHovered || isSelected;

              return (
                <g
                  key={edge.id}
                  className="group"
                  // Hover is tracked on the whole edge group (hit stroke
                  // and visible line). The × control lives in the
                  // top-layer overlay rendered after the node cards and
                  // re-asserts this same hover id on its own
                  // mouseenter, so moving the cursor from the line onto
                  // the × never drops hover mid-approach.
                  onMouseEnter={() => setHoveredEdgeId(edge.id)}
                  onMouseLeave={() => setHoveredEdgeId((prev) => (prev === edge.id ? null : prev))}
                >
                  {/* Invisible wide hit stroke: the edge's real click
                      target (20px), far more forgiving than the 2.5px
                      visible line. Clicking it selects the edge. */}
                  <path
                    data-testid={`edge-hit-${edge.id}`}
                    data-edge-selected={isSelected ? 'true' : undefined}
                    d={pathD}
                    fill="none"
                    stroke="transparent"
                    strokeWidth="20"
                    className="pointer-events-auto cursor-pointer"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setSelectedEdgeId(edge.id);
                      setSelectedNodeId(null);
                    }}
                  />

                  {/* Main visible connection line */}
                  <path
                    d={pathD}
                    fill="none"
                    stroke={isHighlighted ? '#4f46e5' : '#6366f1'}
                    strokeWidth={isHighlighted ? '3.5' : '2.5'}
                    markerEnd="url(#arrowhead)"
                    className="transition-all"
                  />

                  {/* The × delete handle for this edge renders in the
                      top-layer overlay after the node cards (below),
                      not here: this SVG paints at z-10, BELOW the cards
                      (z-20), so a card dragged over the midpoint would
                      swallow the press. See the overlay for the full
                      positioning contract. */}
                </g>
              );
            })}

            {/* Active Live Drag-to-Connect Bezier Path */}
            {connectingSourceId && connectionCursorPos && (
              <g>
                {(() => {
                  const srcPos = getOutputHandlePos(connectingSourceId);
                  const tgtPos = connectionCursorPos;
                  const dx = Math.max(50, Math.abs(tgtPos.x - srcPos.x) * 0.5);
                  const dragPathD = `M ${srcPos.x} ${srcPos.y} C ${srcPos.x + dx} ${srcPos.y}, ${tgtPos.x - dx} ${tgtPos.y}, ${tgtPos.x} ${tgtPos.y}`;

                  return (
                    <path
                      d={dragPathD}
                      fill="none"
                      stroke="#4f46e5"
                      strokeWidth="3"
                      strokeDasharray="6 4"
                      className="animate-pulse"
                      markerEnd="url(#arrowhead)"
                    />
                  );
                })()}
              </g>
            )}
          </svg>

          {/* START / TRIGGER BUBBLE */}
          <div
            style={{ left: startNodePos.x, top: startNodePos.y }}
            className="absolute z-20 flex items-center justify-between w-28 h-20 px-3 py-2 rounded-2xl bg-emerald-50 border-2 border-emerald-500 text-emerald-700 font-extrabold text-xs shadow-lg shadow-emerald-500/10 group select-none"
          >
            <div className="flex flex-col items-center justify-center w-full">
              <div className="p-1.5 rounded-full bg-emerald-500 text-white mb-1 shadow-xs">
                <Play size={12} className="fill-white" />
              </div>
              <span className="text-[10px] tracking-wider uppercase">START</span>
            </div>

            {/* Start Node Connection Handle (28px hit area, 16px visual dot) */}
            {!readOnly && (
              <div
                onPointerDown={(e) => handleOutputHandlePointerDown(e, 'start')}
                className="group absolute -right-3.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center cursor-crosshair z-30"
                title="Drag to connect trigger to first action"
              >
                <span className="block w-4 h-4 rounded-full bg-emerald-500 border-2 border-white shadow-md group-hover:scale-125 transition-transform" />
              </div>
            )}
          </div>

          {/* ACTION BUBBLE CARDS */}
          {nodes.map((node) => {
            const Icon = ACTION_ICONS[node.data.actionType] || FileText;
            const delayMin =
              (node.data.delayValue || 0) *
              (node.data.delayUnit === 'hours' ? 60 : node.data.delayUnit === 'days' ? 1440 : 1);

            const isSelected = selectedNodeId === node.id;
            const sequenceNum = sequenceMap.get(node.id) || '?';
            const isTargetHovered = hoveredTargetId === node.id;
            const cardPos = effectiveNodePos(node);
            const isDragging = dragPos !== null && dragPos.id === node.id;

            return (
              <div
                key={node.id}
                data-testid={`automation-node-${node.id}`}
                style={{
                  left: cardPos.x,
                  top: cardPos.y,
                  width: NODE_WIDTH,
                }}
                onPointerDown={(e) => handleNodePointerDown(e, node.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedNodeId(node.id);
                }}
                className={`group absolute ${isDragging ? 'z-30' : 'z-20'} rounded-2xl p-4 bg-white border transition-shadow ${
                  readOnly ? 'cursor-pointer' : isDragging ? 'cursor-grabbing' : 'cursor-grab active:cursor-grabbing'
                } ${
                  isSelected
                    ? 'border-indigo-600 ring-2 ring-indigo-500/30 shadow-xl'
                    : 'border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md'
                }`}
              >
                {/* Input Connection Handle (Left) — 28px hit area, 16px visual dot */}
                {!readOnly && (
                  <div
                    data-input-handle-id={node.id}
                    className="absolute -left-3.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center z-30"
                    title="Input connection handle"
                  >
                    <span
                      className={`block w-4 h-4 rounded-full border-2 transition-transform ${
                        isTargetHovered
                          ? 'border-emerald-500 bg-emerald-100 ring-4 ring-emerald-300/50 scale-125'
                          : 'border-indigo-600 bg-white shadow-xs'
                      }`}
                    />
                  </div>
                )}

                {/* Output Connection Handle (Right) — 28px hit area, 16px visual dot */}
                {!readOnly && (
                  <div
                    onPointerDown={(e) => handleOutputHandlePointerDown(e, node.id)}
                    className="group/handle absolute -right-3.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center cursor-crosshair z-30"
                    title="Drag to connect to next action step"
                  >
                    <span className="block w-4 h-4 rounded-full bg-brand-primary border-2 border-white shadow-md group-hover/handle:scale-125 transition-transform" />
                  </div>
                )}

                {/* Delay Badge */}
                {delayMin > 0 && (
                  <div className="absolute -top-3 left-4 px-2.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-black flex items-center gap-1 shadow-2xs">
                    <Clock size={10} />
                    Wait {node.data.delayValue} {node.data.delayUnit}
                  </div>
                )}

                {/* Card Top Row */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="relative shrink-0">
                    <div className="p-2 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-600">
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className="absolute -bottom-1 -right-1 min-w-[16px] h-[16px] px-0.5 rounded-full bg-brand-primary text-white text-[11px] font-black flex items-center justify-center">
                      {sequenceNum}
                    </span>
                  </div>

                  {/* Node Actions Bar */}
                  {!readOnly && (
                    <div className="flex items-center gap-1 opacity-90 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicate(node.id);
                        }}
                        className="p-1 text-slate-400 hover:text-slate-800 rounded-lg hover:bg-slate-100 transition-colors"
                        title="Duplicate Node"
                      >
                        <Copy size={13} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingNode(node);
                          setIsModalOpen(true);
                        }}
                        className="p-1 text-slate-400 hover:text-slate-800 rounded-lg hover:bg-slate-100 transition-colors"
                        title="Configure Action Settings"
                      >
                        <Settings size={13} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveNode(node.id);
                        }}
                        className="p-1 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors"
                        title="Delete Action Node"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Title & Action Summary */}
                <h5 className="text-xs font-black text-slate-900 mb-0.5 truncate">
                  {node.data.label}
                </h5>
                <p className="text-[11px] text-slate-500 font-semibold truncate leading-tight">
                  {generateNodeSummary(node.data)}
                </p>

                {/* Validation Warning */}
                {node.data.validationError && (
                  <div className="mt-2 p-1.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-[10px] flex items-center gap-1">
                    <AlertTriangle size={11} className="shrink-0" />
                    <span className="truncate">{node.data.validationError}</span>
                  </div>
                )}
              </div>
            );
          })}

          {/* Empty Flow Visual Guide */}
          {nodes.length === 0 && (
            <div
              style={{ left: startNodePos.x + 180, top: startNodePos.y - 20 }}
              className="absolute z-20 flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 bg-white/80 rounded-2xl text-center max-w-sm backdrop-blur-xs shadow-xs"
            >
              <Unlink size={28} className="text-slate-400 mb-2" />
              <p className="text-xs font-extrabold text-slate-700 mb-1">No Automation Actions Connected</p>
              <p className="text-[11px] text-slate-500 mb-3">
                Click "Add Action" above or drag from the Start trigger handle to begin building your visual flow.
              </p>
              {!readOnly && (
                <button
                  onClick={() => setAddMenuOpen(true)}
                  className="px-3.5 py-1.5 rounded-xl bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-bold transition-all shadow-sm cursor-pointer"
                >
                  Add First Action Step
                </button>
              )}
            </div>
          )}

          {/* Edge Delete Handles ("×") — TOP LAYER, above the cards.
              The edges SVG paints at z-10 and the node cards at z-20,
              so inside that SVG the × could never be the topmost
              element at the midpoint: a card dragged over it swallowed
              the press (and cleared the edge selection instead). This
              overlay sits inside the SAME pan/zoom-transformed
              container (identical coordinate space and render pass as
              the edge paths), after the cards in DOM order, at z-30 —
              the control below is genuinely topmost at its location.
              It is pointer-events-none as a layer; each control
              re-enables pointer events on itself only.
              A control renders for every edge that is hovered OR
              selected — persistently while selected, exactly the
              reachability PR #1359 established. */}
          {!readOnly && (
            <svg
              data-testid="edge-delete-layer"
              className="absolute inset-0 w-full h-full pointer-events-none z-30"
            >
              {overlayEdges.map((edge) => {
                const isHovered = hoveredEdgeId === edge.id;
                const isSelected = selectedEdgeId === edge.id;
                if (!isSelected && !isHovered) return null;

                const srcPos = getOutputHandlePos(edge.source);
                const tgtPos = getInputHandlePos(edge.target);
                const midX = (srcPos.x + tgtPos.x) / 2;
                const midY = (srcPos.y + tgtPos.y) / 2;

                return (
                  <g
                    key={edge.id}
                    data-testid={`edge-delete-${edge.id}`}
                    role="button"
                    aria-label="Remove connection"
                    // POSITION CONTRACT — read before touching: the
                    // position lives ONLY in this inline style, never
                    // in the SVG transform attribute. The global
                    // stylesheet (src/index.css) transitions `transform`
                    // (200ms spring) on every [role="button"] AND
                    // overrides it while pressed:
                    //   [role="button"]:not(:disabled):active {
                    //     transform: translateY(1px) scale(0.99); }
                    // The transform ATTRIBUTE is that same CSS
                    // property at presentation-attribute priority, so
                    // with attribute positioning every real-browser
                    // press animated the control in from the SVG
                    // origin ("jumping from the left"), teleported it
                    // to the origin for the whole :active press, and
                    // the pointerup landed on a different element —
                    // the click never reached this control and
                    // disconnect never fired (jsdom applies no CSS,
                    // which is why synthetic-event tests passed).
                    // Inline style outranks both global rules (neither
                    // is !important), and transition:'none' keeps the
                    // control glued to the midpoint: it appears at the
                    // midpoint on the first render, with no slide,
                    // and midX/midY derive from this same render's
                    // graph (drag positions included), so it tracks
                    // pan/zoom/node drags in the same frame.
                    style={{
                      transform: `translate(${midX}px, ${midY}px)`,
                      transition: 'none',
                    }}
                    className="pointer-events-auto cursor-pointer"
                    // Hover continuity across the layer boundary:
                    // leaving the line fires the edge group's
                    // mouseleave; entering this control re-asserts the
                    // same edge's hover, so the approach never drops
                    // the control mid-flight.
                    onMouseEnter={() => setHoveredEdgeId(edge.id)}
                    onMouseLeave={() =>
                      setHoveredEdgeId((prev) => (prev === edge.id ? null : prev))
                    }
                    // DELETION FIRES ON POINTERDOWN, not click. A
                    // real-browser press is pointerdown -> (window
                    // gesture listeners run) -> pointerup -> click,
                    // and anything in that chain — the press-transform
                    // teleport above all — can retarget or drop the
                    // click. Removing at pointerdown, with propagation
                    // stopped before the canvas gesture machine can
                    // see the press, makes one press delete, always.
                    // The click handler below is a backstop only: in
                    // a real browser the control unmounts with the
                    // edge on pointerdown, so it never receives the
                    // click; if it ever did, the removal is a no-op
                    // filter over an already-removed id.
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleRemoveEdge(edge.id);
                      setSelectedEdgeId(null);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveEdge(edge.id);
                      setSelectedEdgeId(null);
                    }}
                  >
                    <circle r="18" fill="transparent" />
                    <circle r="12" fill="#ffffff" stroke="#e2e8f0" strokeWidth="1.5" className="shadow-md" />
                    <circle r="10" fill="#fee2e2" className="hover:fill-rose-500 transition-colors" />
                    <text
                      x="0"
                      y="4"
                      textAnchor="middle"
                      fontSize="12"
                      fontWeight="bold"
                      fill="#ef4444"
                      className="select-none pointer-events-none"
                    >
                      ×
                    </text>
                  </g>
                );
              })}
            </svg>
          )}
        </div>
      </div>

      {/* Node Config Modal Drawer */}
      <AutomationNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveNodeData}
        initialData={editingNode?.data}
      />
    </div>
  );
};
